import { useState } from "react";
import type { ClipboardEvent } from "react";
import { XACTIMATE_CATEGORIES, FORMULA_UNIT_VALUES } from "@rebuild/shared";
import { NewMappingFormState } from "./types";
import { UnmappedPermutationGroup } from "../../../../../utils/validateXactimateMappings";
import { SectionHeader } from "./shared";
import { ACTIVITY_OPTIONS } from "./constants";

// A flat row in the table. Each row reads its form state from `perm` and
// writes field changes to `updatePerms` (1 for individual/expanded rows,
// all group perms for a collapsed group row).
type TableRow =
  | { kind: "individual"; perm: string }
  | { kind: "collapsed-group"; representative: string; perms: string[] }
  | { kind: "expanded-group"; representative: string; perm: string; isFirst: boolean };

interface UnmappedPermutationsSectionProps {
  newMappingForms: Map<string, NewMappingFormState>;
  unmappedGroups: UnmappedPermutationGroup[];
  expandedGroupKeys: Set<string>;
  onUpdateGroup: (perms: string[], patch: Partial<NewMappingFormState>) => void;
  onToggleGroup: (representative: string) => void;
  onApplyToAll: (cat: string, variable: string, activity: string, quantity: string) => void;
  onSaveAll: () => void;
}

export const UnmappedPermutationsSection = ({
  newMappingForms,
  unmappedGroups,
  expandedGroupKeys,
  onUpdateGroup,
  onToggleGroup,
  onApplyToAll,
  onSaveAll,
}: UnmappedPermutationsSectionProps) => {
  const [bulkCat, setBulkCat] = useState("");
  const [bulkVariable, setBulkVariable] = useState("");
  const [bulkActivity, setBulkActivity] = useState("");
  const [bulkQuantity, setBulkQuantity] = useState("");

  // Flat list of visible table rows, respecting collapse/expand state.
  const rows: TableRow[] = unmappedGroups.flatMap((group): TableRow[] => {
    if (group.type === "individual") {
      return [{ kind: "individual", perm: group.perm }];
    }
    if (expandedGroupKeys.has(group.representative)) {
      return group.perms.map((perm, i) => ({
        kind: "expanded-group" as const,
        representative: group.representative,
        perm,
        isFirst: i === 0,
      }));
    }
    return [{ kind: "collapsed-group", representative: group.representative, perms: group.perms }];
  });

  // Which perms will actually be saved (one per collapsed group, all per expanded).
  const effectivePerms = unmappedGroups.flatMap((group) => {
    if (group.type === "individual") return [group.perm];
    if (expandedGroupKeys.has(group.representative)) return group.perms;
    return [group.representative];
  });

  const effectiveForms = effectivePerms.map((p) => newMappingForms.get(p)).filter(Boolean) as NewMappingFormState[];
  const completeCount = effectiveForms.filter((f) => f.cat && f.variable && f.quantity).length;
  const incompleteCount = effectivePerms.length - completeCount;
  const isSavingAny = effectiveForms.some((f) => f.saving);

  // Paste-to-fill-down on the SEL column, operating on visible rows.
  const handleSelPaste = (e: ClipboardEvent<HTMLInputElement>, rowIndex: number) => {
    const values = e.clipboardData.getData("text").split(/\r?\n/).map((v) => v.trim()).filter(Boolean);
    if (values.length <= 1) return;
    e.preventDefault();
    values.forEach((value, offset) => {
      const row = rows[rowIndex + offset];
      if (!row) return;
      const updatePerms =
        row.kind === "collapsed-group" ? row.perms : [row.kind === "individual" ? row.perm : row.perm];
      onUpdateGroup(updatePerms, { variable: value });
    });
  };

  return (
    <section>
      <SectionHeader label="Unmapped Permutations" count={newMappingForms.size} status="bad" />
      <p className="text-xs text-stone-400 mb-3">
        These permutations have no Xactimate mapping yet. Set CAT and SEL in bulk, then adjust Activity and Unit per row.
      </p>

      {/* Bulk apply bar */}
      <div className="flex items-center gap-2 mb-3 px-3 py-2.5 bg-stone-50 rounded-lg border border-stone-200">
        <span className="text-xs text-stone-500 shrink-0">Apply to all rows:</span>
        <select
          className="border rounded px-2 py-1 text-xs w-32"
          value={bulkCat}
          onChange={(e) => setBulkCat(e.target.value)}
        >
          <option value="">CAT</option>
          {XACTIMATE_CATEGORIES.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <input
          className="border rounded px-2 py-1 text-xs w-28"
          placeholder="SEL"
          value={bulkVariable}
          onChange={(e) => setBulkVariable(e.target.value)}
        />
        <select
          className="border rounded px-2 py-1 text-xs w-32"
          value={bulkActivity}
          onChange={(e) => setBulkActivity(e.target.value)}
        >
          <option value="">Activity</option>
          {ACTIVITY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select
          className="border rounded px-2 py-1 text-xs w-24"
          value={bulkQuantity}
          onChange={(e) => setBulkQuantity(e.target.value)}
        >
          <option value="">Unit</option>
          {FORMULA_UNIT_VALUES.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
        <button
          className="btn rounded bg-stone-700 text-white text-xs px-3 py-1 disabled:opacity-40"
          disabled={!bulkCat && !bulkVariable && !bulkActivity && !bulkQuantity}
          onClick={() => onApplyToAll(bulkCat, bulkVariable, bulkActivity, bulkQuantity)}
        >
          Apply to all →
        </button>
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-stone-50 border-b">
            <tr>
              <th className="text-left px-3 py-2 font-medium text-stone-500">Permutation</th>
              <th className="text-left px-2 py-2 font-medium text-stone-500 w-28">CAT</th>
              <th className="text-left px-2 py-2 font-medium text-stone-500 w-24">SEL</th>
              <th className="text-left px-2 py-2 font-medium text-stone-500 w-32">Activity</th>
              <th className="text-left px-2 py-2 font-medium text-stone-500 w-24">Unit</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {rows.map((row, rowIndex) => {
              const perm = row.kind === "collapsed-group" ? row.representative : row.perm;
              const form = newMappingForms.get(perm);
              if (!form) return null;

              const updatePerms = row.kind === "collapsed-group" ? row.perms : [perm];
              const isDimmed = row.kind === "expanded-group" && !row.isFirst;
              const rowKey = row.kind === "collapsed-group" ? row.representative : row.perm;

              return (
                <tr key={rowKey} className={form.saving ? "opacity-50 pointer-events-none" : ""}>
                  <td className="px-3 py-2 font-mono text-stone-700">
                    {row.kind === "collapsed-group" ? (
                      <div className="flex items-center gap-2">
                        <span>{row.representative}</span>
                        <button
                          onClick={() => onToggleGroup(row.representative)}
                          className="text-xs text-stone-400 hover:text-stone-600 shrink-0"
                        >
                          ▸ +{row.perms.length - 1} action variant{row.perms.length - 1 !== 1 ? "s" : ""}
                        </button>
                      </div>
                    ) : row.kind === "expanded-group" && row.isFirst ? (
                      <div className="flex items-center gap-2">
                        <span>{row.perm}</span>
                        <button
                          onClick={() => onToggleGroup(row.representative)}
                          className="text-xs text-stone-400 hover:text-stone-600 shrink-0"
                        >
                          ▾ collapse
                        </button>
                      </div>
                    ) : (
                      <span className={isDimmed ? "text-stone-400" : undefined}>{perm}</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5">
                    <select
                      className="border rounded px-1.5 py-1 w-full text-xs"
                      value={form.cat}
                      onChange={(e) => onUpdateGroup(updatePerms, { cat: e.target.value })}
                    >
                      <option value="">—</option>
                      {XACTIMATE_CATEGORIES.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-2 py-1.5">
                    <input
                      className="border rounded px-1.5 py-1 w-full text-xs"
                      value={form.variable}
                      onChange={(e) => onUpdateGroup(updatePerms, { variable: e.target.value })}
                      onPaste={(e) => handleSelPaste(e, rowIndex)}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <select
                      className="border rounded px-1.5 py-1 w-full text-xs"
                      value={form.activity}
                      onChange={(e) => onUpdateGroup(updatePerms, { activity: e.target.value })}
                    >
                      <option value="">—</option>
                      {ACTIVITY_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-2 py-1.5">
                    <select
                      className="border rounded px-1.5 py-1 w-full text-xs"
                      value={form.quantity}
                      onChange={(e) => onUpdateGroup(updatePerms, { quantity: e.target.value })}
                    >
                      <option value="">—</option>
                      {FORMULA_UNIT_VALUES.map((m) => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-3">
        {incompleteCount > 0 && (
          <p className="text-xs text-stone-400">
            {incompleteCount} incomplete row{incompleteCount !== 1 ? "s" : ""} will be skipped.
          </p>
        )}
        <button
          className="btn rounded bg-green-600 text-white text-xs px-4 py-1.5 disabled:opacity-50 ml-auto"
          onClick={onSaveAll}
          disabled={isSavingAny || completeCount === 0}
        >
          {isSavingAny ? "Saving…" : `Save ${completeCount} Mapping${completeCount !== 1 ? "s" : ""}`}
        </button>
      </div>
    </section>
  );
};
